import type { AxiosResponse } from "axios";

const headerValue = (
  headers: Record<string, unknown> | undefined,
  name: string,
): string | undefined => {
  if (!headers) return undefined;
  const lower = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lower) {
      const value = headers[key];
      return typeof value === "string" ? value : undefined;
    }
  }
  return undefined;
};

/**
 * Does this REST response mean "the session is no longer accepted"?
 *
 * A 401 answers that on its own, and it has to: the previous implementation
 * required a `www-authenticate` header naming `invalid_token` or `token expired`,
 * and neither condition can ever hold against this API. Measured against
 * `blocksapi.selise.club` on 2026-08-24:
 *
 *   - the header it actually sends is `www-authenticate: Bearer` — a bare scheme
 *     with no reason phrase, so both patterns miss; and
 *   - the response carries no `Access-Control-Expose-Headers`, and
 *     `www-authenticate` is not one of the CORS-safelisted response headers, so
 *     in a browser it is not readable from JS at all.
 *
 * So this returned false for every real 401. The response interceptor took that
 * as "not an auth problem" and rejected immediately — never calling
 * `refreshSession()` and never calling `handleAuthFailure()`. The session was
 * therefore neither renewed nor ended: every request just kept failing while the
 * app sat there, which is the wall of 401s across `/iam/me`, `/organizations/my`,
 * `/iam/permissions` and `/data/v4/gateway`.
 *
 * The header check is kept as an additional signal for non-401 responses, where
 * some gateways report an expired token with a 400.
 */
export const isRestTokenExpired = (
  response: AxiosResponse | undefined,
): boolean => {
  if (response?.status === 401) return true;

  const wwwAuthenticate = headerValue(
    response?.headers as Record<string, unknown> | undefined,
    "www-authenticate",
  );
  if (!wwwAuthenticate) return false;
  return (
    /invalid_token/i.test(wwwAuthenticate) ||
    /token expired/i.test(wwwAuthenticate)
  );
};

/**
 * Routes reachable WITHOUT a session.
 *
 * A 401 from one of these is the endpoint's own answer — "wrong password", "bad
 * code" — not an expired session, and `isRestTokenExpired` cannot tell the
 * difference because it (correctly) treats a bare 401 as expiry: this API sends
 * no readable `www-authenticate` reason.
 *
 * Without this list, signing in with a wrong password did all of the following
 * instead of showing "Invalid email or password":
 *
 *   1. tried `refreshSession()` for a session that does not exist yet;
 *   2. on its failure called `handleAuthFailure()` → `authEvents.triggerLogout()`
 *      → `router.replace("/(auth)/login")`, which reset the form and wiped the
 *      error the UI had just set;
 *   3. rejected with the REFRESH error, so `err.response` was gone and the
 *      mutation's `onError` could not read `error_description`
 *      ("User name or password invalid") or even the 401 status — it fell
 *      through to the generic branch; and
 *   4. worst of it: on the runs where the refresh *did* resolve, it retried the
 *      login itself, spending real failed-attempt budget on the account.
 */
const PRE_SESSION_PATHS = [
  "/auth/login", // also covers /auth/login-options
  "/auth/recover",
  "/auth/reset-password",
  "/auth/activate",
  "/auth/resend-activation",
  "/auth/refresh",
  "/auth/logout",
  "/auth/social/callback",
  "/idp/callback",
  "/oidc/callback",
] as const;

export const isPreSessionRequest = (url: string | undefined): boolean =>
  !!url && PRE_SESSION_PATHS.some((path) => url.includes(path));

export const isGraphQLAuthError = (error: unknown): boolean => {
  const err = error as {
    response?: { errors?: unknown };
    errors?: unknown;
  };
  const errors = err?.response?.errors ?? err?.errors;
  if (!Array.isArray(errors)) return false;
  return errors.some((entry) => {
    const item = entry as {
      message?: unknown;
      extensions?: { code?: unknown };
    };
    return (
      item?.extensions?.code === "AUTH_NOT_AUTHENTICATED" ||
      item?.message === "User is not authenticated."
    );
  });
};

export const isAiTokenExpired = (data: unknown): boolean => {
  const body = data as { detail?: unknown };
  return body?.detail === "Token expired";
};

export const isRefreshTokenExpired = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 401;
};
